// import React and necessary hooks
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { scrapingService } from '../services/scrapingService';
import Chatbot from '../components/Chatbot';
// import { TEXAS_COUNTIES } from '../constants/texasCounties';

// Cost Analytics Panel
import CostAnalyticsPanel from '../components/CostAnalyticsPanel';

// Data Query Panel
import DataQueryPanel from '../components/DataQueryPanel';

// BatchData V3 weak-match recovery panel
import BatchDataV3Panel from '../components/BatchDataV3Panel';

// Phase 7: BatchData V3 Leads workflow (feature-flagged, separate from V3 Recovery above)
import V3LeadsJobProgress from '../components/V3LeadsJobProgress';
import V3UncertainRecoveryPanel from '../components/V3UncertainRecoveryPanel';
import useEffectiveLeadsLoad from '../hooks/useEffectiveLeadsLoad';
import {
  isV3LeadsEnabled, storeActiveJobId, readActiveJobId, clearActiveJobId, isTerminalWorkflow,
} from '../utils/v3LeadsHelpers';

// New icons import
import { FaSignOutAlt, FaUsers, FaDatabase, FaBars, FaTimes, FaDollarSign, FaCheckSquare, FaRobot, FaBuilding } from 'react-icons/fa';
import { AiFillThunderbolt } from 'react-icons/ai';
import { MdVerified } from 'react-icons/md';

// New EmailsTable component imports
import EmailsTable from '../components/EmailsTable';

// Multi-county selection components
import MultiCountySelect from '../components/MultiCountySelect';
import CountyInfoCard from '../components/CountyInfoCard';

// Component that rotates through realistic server processing messages
function BatchDataProcessingStatus() {
  const [messageIndex, setMessageIndex] = useState(0);
  
  const processingMessages = [
    "Checking database for existing records...",
    "Filtering records that need enrichment...",
    "Connecting to BatchData API...",
    "Processing property addresses...",
    "Finding owner information...",
    "Looking up phone numbers...",
    "Retrieving email addresses...",
    "Validating contact information...",
    "Saving enriched records to database in real-time...", // New message reflecting real-time saving
    "Processing next batch of records...", // New message for batch processing
    "Saving enrichment results as they complete...", // New message emphasizing real-time
    "Building final response with combined results...",
    "Almost done, finalizing results..."
  ];
  
  useEffect(() => {
    // Rotate through messages every 3.5 seconds
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % processingMessages.length);
    }, 3500);
    
    return () => clearInterval(interval);
  }, [processingMessages.length]);
  
  return (
    <>
      <div className="flex items-center">
        {processingMessages[messageIndex]}
        <span className="inline-block animate-pulse ml-1">...</span>
      </div>
      <div className="text-xs text-blue-500 mt-1">
        Records are saved to the database as they're enriched
      </div>
    </>
  );
}

function MainApp() {
    // Auth context
    const { logout, currentUser } = useAuth();


  const isDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

  // Debugging state
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState('');
  const [counties, setCounties] = useState([]);
  
  // UPDATED: Multi-county selection (replaces single countyId/countyName)
  const [selectedCounties, setSelectedCounties] = useState([]); // Array of { county_id, county_name }
  
  // UPDATED: Per-county statistics (keyed by county_id)
  // Contains: loading, loaded, nonEnrichedCount, enrichedCount, filteredNonEnriched, filteredEnriched, 
  // minAcres, maxAcres, unverifiedCount, totalEmailCount, verifiedEmailCount
  const [countyStats, setCountyStats] = useState({});
  const countyStatsRef = useRef(countyStats);

  // Read the latest committed stats for debounce timing without making the
  // stats-loading effect retrigger on its own state updates.
  useEffect(() => {
    countyStatsRef.current = countyStats;
  }, [countyStats]);
  
  // UPDATED: Enrichment progress tracking for multi-county
  const [enrichmentProgress, setEnrichmentProgress] = useState({
    isProcessing: false,
    currentCountyIndex: -1,
    currentCountyName: '',
    completedCounties: [],
    totalCounties: 0
  });
  
  // Backward compatibility: derived values for single county selection
  const countyId = selectedCounties.length === 1 ? selectedCounties[0].county_id : (selectedCounties.length > 0 ? selectedCounties[0].county_id : '');
  const countyName = selectedCounties.length === 1 ? selectedCounties[0].county_name : (selectedCounties.length > 0 ? `${selectedCounties.length} counties` : '');

  // Remove limit state as it's no longer used (newly removed)
  // const [limit, setLimit] = useState(30);

  // Acreage filter states (newly added)
  const [acresFilterMode, setAcresFilterMode] = useState('between'); // 'atleast' | 'atmax' | 'between'

  const [minAcres, setMinAcres] = useState(50);
  const [maxAcres, setMaxAcres] = useState('');

  // NEW: Add state for county acreage range display
  const [countyMinAcres, setCountyMinAcres] = useState(null);
  const [countyMaxAcres, setCountyMaxAcres] = useState(null);

  // Enriched records state (newly added)
  const [, setEnrichedRecords] = useState([]);

  // All enriched records state (newly added)
  const [allEnrichedRecords, setAllEnrichedRecords] = useState([]);

  // All non-enriched records state (for mailing_address lookup)
  const [allNonEnrichedRecords, setAllNonEnrichedRecords] = useState([]);

  // Debugging state (for raw data loading) newly added variable
  const [loadingRawData, setLoadingRawData] = useState(false);

  // Leads and selection state
  const [leads] = useState([]); // enriched rows
  const [onlyWithEmail] = useState(false);

  // Modal and tab states
  const [showSendModal] = useState(false);
  const [activeTab, setActiveTab] = useState('leads');

  // NEW: Add state for filtered counts
  const [, setFilteredNonEnrichedCount] = useState(null);
  const [, setFilteredEnrichedCount] = useState(null);
  const [, setLoadingFilteredCounts] = useState(false);

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Add state for non enriched counts (Newly added)
  const [, setNonEnrichedCount] = useState(null);
  const [, setLoadingCount] = useState(false);

  // Add state for enriched counts (Newly added)
  const [, setEnrichedCount] = useState(null);
  const [, setLoadingEnrichedCount] = useState(false);

  // Enrichment options state (BatchData enrichment + ZeroBounce verification)
  const [enableBatchDataEnrichment, setEnableBatchDataEnrichment] = useState(true);
  const [enableZeroBounceVerification, setEnableZeroBounceVerification] = useState(true);

  // Phase 7: BatchData V3 Leads workflow (feature-flagged rollout/rollback switch).
  // When OFF, none of this state is ever read/written — the legacy path above is untouched.
  const v3LeadsEnabled = isV3LeadsEnabled();
  const [includeEntities, setIncludeEntities] = useState(true); // "Include business/entity contacts", default checked
  const [verifyAllLegacyEmails, setVerifyAllLegacyEmails] = useState(true);
  const [v3Preparing, setV3Preparing] = useState(false);
  const [v3ActiveJobId, setV3ActiveJobId] = useState(null);
  const [workflowJob, setWorkflowJob] = useState(null);
  const [workflowScope, setWorkflowScope] = useState(null);
  const [countyStatsRefresh, setCountyStatsRefresh] = useState(0);
  const loadedWorkflow = useRef(null);
  const pendingEmailScope = useMemo(() => ({
    countyIds: selectedCounties.map((c) => c.county_id), filterMode: acresFilterMode,
    minAcres: acresFilterMode === 'atmax' ? null : (Number(minAcres) || null),
    maxAcres: acresFilterMode === 'atleast' ? null : (Number(maxAcres) || null),
  }), [selectedCounties, acresFilterMode, minAcres, maxAcres]);
  const effectiveLeadsLoad = useEffectiveLeadsLoad(workflowScope || pendingEmailScope, scrapingService.fetchAllEffectiveLeads);
  const effectiveLeadRows = effectiveLeadsLoad.rows;
  const loadingEffectiveLeads = effectiveLeadsLoad.status === 'loading';
  const workflowBusy = v3Preparing || (v3ActiveJobId && (!workflowJob || !isTerminalWorkflow(workflowJob)));
  const { load: loadWorkflowLeads } = effectiveLeadsLoad;
  useEffect(() => {
    if (!workflowJob || !workflowScope || !isTerminalWorkflow(workflowJob)) return;
    const key = workflowJob.jobId + ':' + (workflowJob.workflow?.explicitVerificationRetryAt || '');
    if (loadedWorkflow.current === key) return;
    loadedWorkflow.current = key;
    loadWorkflowLeads();
    setCountyStatsRefresh((version) => version + 1);
  }, [workflowJob, workflowScope, loadWorkflowLeads]);

  const displayedLeads = useMemo(() => {
    const base = Array.isArray(leads) ? leads : [];
    return onlyWithEmail ? base.filter((l) => l.email) : base;
  }, [leads, onlyWithEmail]);

    // Handle logout click
  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      // Router will automatically redirect to login
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  useEffect(() => {
    // Load states on mount
    (async () => {
      try {
        const s = await scrapingService.fetchStates();
        setStates(Array.isArray(s) ? s : []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    // When state changes, load counties and clear selection
    (async () => {
      if (!stateId) { 
        setCounties([]); 
        setSelectedCounties([]); 
        setCountyStats({});
        return; 
      }
      try {
        const c = await scrapingService.fetchCounties(stateId);

        // Debugging output
        console.log('Fetched counties:', c);
        // End debugging output
        
        setCounties(Array.isArray(c) ? c : []);
        setSelectedCounties([]);
        setCountyStats({});
      } catch (e) {
        console.error(e);
      }
    })();
  }, [stateId]);

  useEffect(() => {
    // When county changes, load acreage range
    (async () => {
      if (!countyId) { 
        setMinAcres(50); 
        setMaxAcres(''); 
        setCountyMinAcres(null);
        setCountyMaxAcres(null);
        return; 
      }
      try {
        const range = await scrapingService.fetchAcreageRange(countyId);
        // Store the county's actual range for display
        setCountyMinAcres(range?.min ?? 50);
        setCountyMaxAcres(range?.max ?? null);
        // Set default filter values - maxAcres stays empty (unlimited)
        setMinAcres(50);
        setMaxAcres('');
      } catch (e) {
        console.error(e);
        setCountyMinAcres(null);
        setCountyMaxAcres(null);
      }
    })();
  }, [countyId]);

  // ...existing code...

  // Add useEffect to fetch count when county changes
  useEffect(() => {
    const fetchCountyStats = async () => {
      if (!countyId) {
        setNonEnrichedCount(null);
        return;
      }
      
      setLoadingCount(true);
      try {
        const countResult = await scrapingService.fetchUniqueOwnerNonEnrichedCount(countyId);
        setNonEnrichedCount(countResult.total_count);
      } catch (error) {
        console.error('Error fetching county stats:', error);
        setNonEnrichedCount(null);
      } finally {
        setLoadingCount(false);
      }
    };

    fetchCountyStats();
  }, [countyId]);

  // Add useEffect to fetch enriched count when county changes
  useEffect(() => {
    const fetchEnrichmentStats = async () => {
      if (!countyId) {
        setEnrichedCount(null);
        return;
      }
      
      setLoadingEnrichedCount(true);
      try {
        // You'll need to implement this function in scrapingService.js
        const enrichedResult = await scrapingService.fetchUniqueOwnerEnrichedCount(countyId);
        setEnrichedCount(enrichedResult.total_count);
      } catch (error) {
        console.error('Error fetching enrichment statistics:', error);
        setEnrichedCount(null);
      } finally {
        setLoadingEnrichedCount(false);
      }
    };

    fetchEnrichmentStats();
  }, [countyId]);

  // Debugging output
  // Add this after your existing useEffect hooks (around line 91)
  useEffect(() => {
    if (!displayedLeads.length) return;
    
    // Check for duplicate IDs
    const ids = displayedLeads.map(r => r.id ?? r.unenriched_id ?? `${r.first_name}-${r.last_name}-${r.address}`);
    const uniqueIds = new Set(ids);
    
    if (ids.length !== uniqueIds.size) {
      console.warn("⚠️ DUPLICATE IDs DETECTED!");
      console.log("Total leads:", ids.length);
      console.log("Unique IDs:", uniqueIds.size);
      
      // Find the duplicates
      const counts = {};
      ids.forEach(id => { counts[id] = (counts[id] || 0) + 1; });
      const duplicates = Object.entries(counts).filter(([_, count]) => count > 1);
      console.log("Duplicate IDs:", duplicates);
      
      // Show the duplicate rows
      duplicates.forEach(([dupId, count]) => {
        const dupes = displayedLeads.filter(r => {
          const rowId = r.id ?? r.unenriched_id ?? `${r.first_name}-${r.last_name}-${r.address}`;
          return rowId === dupId;
        });
        console.log(`${count} rows with ID ${dupId}:`, dupes);
      });
    }
  }, [displayedLeads]);
  // End debugging output

  // NEW: useEffect to fetch filtered counts when county or filters change
  useEffect(() => {
    const fetchFilteredCounts = async () => {
      if (!countyId) {
        setFilteredNonEnrichedCount(null);
        setFilteredEnrichedCount(null);
        return;
      }

      setLoadingFilteredCounts(true);
      try {
        // Prepare parameters based on filter mode
        const effectiveMinAcres = acresFilterMode === 'atmax' ? null : minAcres;
        const effectiveMaxAcres = acresFilterMode === 'atleast' ? null : (maxAcres || null);

        // Fetch both counts in parallel
        const [nonEnrichedResult, enrichedResult] = await Promise.all([
          scrapingService.fetchFilteredUniqueOwnerNonEnrichedCount(
            countyId,
            effectiveMinAcres,
            effectiveMaxAcres,
            acresFilterMode
          ),
          scrapingService.fetchFilteredUniqueOwnerEnrichedCount(
            countyId,
            effectiveMinAcres,
            effectiveMaxAcres,
            acresFilterMode
          )
        ]);

        setFilteredNonEnrichedCount(nonEnrichedResult.filtered_count);
        setFilteredEnrichedCount(enrichedResult.filtered_count);
      } catch (error) {
        console.error('Error fetching filtered counts:', error);
        setFilteredNonEnrichedCount(null);
        setFilteredEnrichedCount(null);
      } finally {
        setLoadingFilteredCounts(false);
      }
    };

    // Debounce the fetch to avoid too many API calls while typing
    const debounceTimer = setTimeout(fetchFilteredCounts, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [countyId, minAcres, maxAcres, acresFilterMode]);

  // COMBINED: Load ALL stats for selected counties (basic + filtered)
  // Triggers on county selection AND filter changes
  useEffect(() => {
    let isCancelled = false;
    
    const loadAllCountyStats = async () => {
      if (selectedCounties.length === 0) {
        setCountyStats({});
        return;
      }

      const effectiveMinAcres = acresFilterMode === 'atmax' ? null : minAcres;
      const effectiveMaxAcres = acresFilterMode === 'atleast' ? null : (maxAcres || null);

      // Initialize loading state for all counties
      setCountyStats(prev => {
        const newStats = { ...prev };
        for (const county of selectedCounties) {
          newStats[county.county_id] = { 
            ...newStats[county.county_id],
            loading: true 
          };
        }
        return newStats;
      });

      // Load stats for each county
      for (const county of selectedCounties) {
        if (isCancelled) return;
        
        try {
          // Fetch ALL stats in parallel for this county (basic + filtered)
          const [
            countResult, 
            enrichedCountResult, 
            rangeResult,
            filteredNonEnrichedResult,
            filteredEnrichedResult,
            unverifiedResult,
            emailStatsResult
          ] = await Promise.all([
            scrapingService.fetchUniqueOwnerNonEnrichedCount(county.county_id),
            scrapingService.fetchUniqueOwnerEnrichedCount(county.county_id),
            scrapingService.fetchAcreageRange(county.county_id),
            scrapingService.fetchFilteredUniqueOwnerNonEnrichedCount(
              county.county_id,
              effectiveMinAcres,
              effectiveMaxAcres,
              acresFilterMode
            ),
            scrapingService.fetchFilteredUniqueOwnerEnrichedCount(
              county.county_id,
              effectiveMinAcres,
              effectiveMaxAcres,
              acresFilterMode
            ),
            scrapingService.fetchUnverifiedEnrichedCount(
              county.county_id,
              effectiveMinAcres,
              effectiveMaxAcres,
              acresFilterMode
            ),
            scrapingService.fetchEnrichedEmailStats(
              county.county_id,
              effectiveMinAcres,
              effectiveMaxAcres,
              acresFilterMode
            )
          ]);
          
          if (isCancelled) return;
          
          setCountyStats(prev => ({
            ...prev,
            [county.county_id]: {
              loading: false,
              loaded: true,
              // Basic county stats
              nonEnrichedCount: countResult.total_count,
              enrichedCount: enrichedCountResult.total_count,
              minAcres: rangeResult?.min ?? 0,
              maxAcres: rangeResult?.max ?? null,
              // Filtered stats
              filteredNonEnriched: filteredNonEnrichedResult.filtered_count,
              filteredEnriched: filteredEnrichedResult.filtered_count,
              unverifiedCount: unverifiedResult.unverified_count || 0,
              // Email verification stats
              totalEmailCount: emailStatsResult.total_emails || 0,
              verifiedEmailCount: emailStatsResult.verified_emails || 0
            }
          }));
        } catch (error) {
          console.error(`Error loading stats for ${county.county_name}:`, error);
          if (isCancelled) return;
          setCountyStats(prev => ({
            ...prev,
            [county.county_id]: { 
              ...prev[county.county_id],
              loading: false, 
              loaded: false, 
              error: true 
            }
          }));
        }
      }
    };

    // Debounce only for filter changes (when counties already loaded)
    // For initial county selection, we check if any county is new
    const hasNewCounties = selectedCounties.some(
      county => !countyStatsRef.current[county.county_id]?.loaded
    );
    
    const debounceMs = hasNewCounties ? 0 : 300;
    const debounceTimer = setTimeout(loadAllCountyStats, debounceMs);
    
    return () => {
      clearTimeout(debounceTimer);
      isCancelled = true;
    };
  }, [selectedCounties, minAcres, maxAcres, acresFilterMode, countyStatsRefresh]);

  // ============================================================
  // End states section 





  // Updated function with filter support and MULTI-COUNTY processing
  const getCompleteEnrichedLeads = async () => {
    if (selectedCounties.length === 0) {
      toast.error('Please select at least one county first');
      return;
    }

    setLoadingRawData(true);
    setEnrichmentProgress({
      isProcessing: true,
      currentCountyIndex: 0,
      currentCountyName: selectedCounties[0].county_name,
      completedCounties: [],
      totalCounties: selectedCounties.length
    });
    
    // Combined results from all counties
    let combinedEnrichedRecords = [];
    
    // Prepare effective filter values based on mode
    const effectiveMinAcres = acresFilterMode === 'atmax' ? 0 : minAcres;
    const effectiveMaxAcres = acresFilterMode === 'atleast' ? null : (maxAcres || null);

    try {
      // Process each county sequentially
      for (let i = 0; i < selectedCounties.length; i++) {
        const county = selectedCounties[i];
        
        // Update progress
        setEnrichmentProgress(prev => ({
          ...prev,
          currentCountyIndex: i,
          currentCountyName: county.county_name
        }));
        
        toast(`Processing ${county.county_name} (${i + 1}/${selectedCounties.length})...`);
        console.log(`\n========== PROCESSING COUNTY: ${county.county_name} (${i + 1}/${selectedCounties.length}) ==========`);

        // Step 1: Fetch raw data for this county
        const rawDataResult = await scrapingService.fetchUniqueOwnerNonEnrichedAll(
          county.county_id,
          effectiveMinAcres,
          effectiveMaxAcres,
          acresFilterMode
        );
        
        console.log(`${county.county_name}: Fetched ${rawDataResult?.rows?.length || 0} raw records`);

        // Store non-enriched records for mailing_address lookup
        setAllNonEnrichedRecords(prev => [...prev, ...(rawDataResult?.rows || [])]);

        // Step 2: Fetch already enriched records for this county
        const enrichedResult = await scrapingService.fetchUniqueOwnerEnrichedAll(
          county.county_id,
          effectiveMinAcres,
          effectiveMaxAcres,
          acresFilterMode
        );
        const existingRecords = enrichedResult?.rows || [];
        
        console.log(`${county.county_name}: Found ${existingRecords.length} existing enriched records`);
        
        // Create a set of existing base unique IDs
        const existingBaseIds = new Set();
        existingRecords.forEach(record => {
          const baseId = record.unique_id.split('-').slice(0, -1).join('-');
          existingBaseIds.add(baseId);
        });
        
        // Filter raw data to find records that need enrichment
        const rawDataNeedingEnrichment = rawDataResult.rows.filter(row => {
          const baseId = row.unique_id.split('-').slice(0, -1).join('-');
          return !existingBaseIds.has(baseId);
        });
        
        console.log(`${county.county_name}: ${rawDataNeedingEnrichment.length} records need enrichment`);
        
        let countyEnrichedRecords = [...existingRecords];
        
        // Only call BatchData API if we have records that need enrichment
        if (rawDataNeedingEnrichment.length > 0) {
          toast(`Enriching ${rawDataNeedingEnrichment.length} records for ${county.county_name}...`);
          
          const enrichmentResult = await scrapingService.processBatchDataEnrichment(
            { rows: rawDataNeedingEnrichment, countyId: county.county_id }, 
            {
              testMode: false,
              sampleSize: null,
              skipValidation: false,
              enrichAllFields: true,
              filterMode: acresFilterMode,
              minAcres: effectiveMinAcres,
              maxAcres: effectiveMaxAcres
            }
          );
          
          if (enrichmentResult?.outputRows?.length > 0) {
            countyEnrichedRecords = [...existingRecords, ...enrichmentResult.outputRows];
            toast.success(`${county.county_name}: Enriched ${enrichmentResult.outputRows.length} new records`);
          }
        } else {
          toast.success(`${county.county_name}: All records already enriched!`);
        }

        // Step 3: Check which records have emails that need verification (if ZeroBounce is enabled)
        if (enableZeroBounceVerification) {
          // Don't filter by zerobounce_verified flag - it's at owner level, not email slot level
          // The backend will handle deduplication and only verify truly unverified email slots
          const recordsWithEmails = countyEnrichedRecords.filter(
            r => r.email_1 || r.email_2 || r.email_3 || r.email_4
          );
          
          // For display purposes, estimate how many might need verification based on flag
          const recordsLikelyVerified = recordsWithEmails.filter(r => r.zerobounce_verified);

          // Show ZeroBounce verification status for this county
          if (recordsWithEmails.length > 0) {
            console.log(`${county.county_name}: ZeroBounce status — ~${recordsLikelyVerified.length}/${recordsWithEmails.length} flagged as verified, sending ${recordsWithEmails.length} to check for any unverified slots`);
            toast(
              `${county.county_name}: Checking ${recordsWithEmails.length} records for any unverified email slots...`,
              { icon: '🔍', duration: 5000 }
            );
          }
          
          // Always send records with emails - backend will efficiently skip already-verified ones
          if (recordsWithEmails.length > 0) {
            toast(
              `${county.county_name}: Sending ${recordsWithEmails.length} records to ZeroBounce for verification...`,
              { icon: '📧', duration: 4000 }
            );
            
            try {
              const verificationResult = await scrapingService.verifyEmailsWithZeroBounce(
                recordsWithEmails,
                county.county_id
              );
              
              if (verificationResult.success && verificationResult.records) {
                const verifiedMap = new Map(
                  verificationResult.records.map(r => [r.unique_id, r])
                );
                
                countyEnrichedRecords = countyEnrichedRecords.map(record => {
                  const verified = verifiedMap.get(record.unique_id);
                  return verified ? { ...record, ...verified } : record;
                });
                
                toast.success(
                  `${county.county_name}: ZeroBounce verified ${verificationResult.verifiedCount || 0} emails` +
                  (verificationResult.skippedCount ? `, ${verificationResult.skippedCount} already verified` : '') +
                  (verificationResult.errors?.length ? `, ${verificationResult.errors.length} errors` : '')
                );
              } else {
                toast.error(`${county.county_name}: ZeroBounce returned unexpected response`);
                console.error('ZeroBounce unexpected response:', verificationResult);
              }
            } catch (verifyError) {
              console.error(`ZeroBounce verification error for ${county.county_name}:`, verifyError);
              toast.error(
                `${county.county_name}: ZeroBounce verification failed — ${verifyError.message}`,
                { duration: 8000 }
              );
            }
          } else {
            toast.success(`${county.county_name}: No records with emails to verify`);
          }
        } else {
          // ZeroBounce verification skipped
          console.log(`${county.county_name}: ZeroBounce verification skipped (disabled by user)`);
        }

        // Add county_name to each record for identification
        countyEnrichedRecords = countyEnrichedRecords.map(r => ({
          ...r,
          county_name: county.county_name,
          county_id: county.county_id
        }));

        // Add to combined results (newest county at the top)
        combinedEnrichedRecords = [...countyEnrichedRecords, ...combinedEnrichedRecords];
        
        // Update UI with combined results so far
        setAllEnrichedRecords([...combinedEnrichedRecords]);
        setEnrichedRecords([...combinedEnrichedRecords]);
        
        // Mark county as completed
        setEnrichmentProgress(prev => ({
          ...prev,
          completedCounties: [...prev.completedCounties, county.county_id]
        }));
        
        console.log(`${county.county_name}: Complete. Total combined records: ${combinedEnrichedRecords.length}`);
      }

      const actionText = enableZeroBounceVerification ? 'Enrichment & verification' : 'Enrichment';
      toast.success(`${actionText} complete! Total: ${combinedEnrichedRecords.length} records from ${selectedCounties.length} ${selectedCounties.length === 1 ? 'county' : 'counties'}`);

    } catch (error) {
      toast.error(error.message || 'Failed to complete lead enrichment');
      console.error('Lead enrichment process failed:', error);
    } finally {
      setLoadingRawData(false);
      setEnrichmentProgress({
        isProcessing: false,
        currentCountyIndex: -1,
        currentCountyName: '',
        completedCounties: [],
        totalCounties: 0
      });
    }
  };

// End 

  // ============================================================
  // Phase 7: BatchData V3 Leads workflow (all gated by v3LeadsEnabled;
  // completely inert — reads/writes nothing — when the flag is off).
  // ============================================================

  // Restore an in-progress job across a page refresh. Never auto-creates a
  // new job; a 404 (stale/deleted job) just clears local storage gracefully.
  useEffect(() => {
    if (!v3LeadsEnabled) return;
    const stored = readActiveJobId();
    if (stored) setV3ActiveJobId(stored);
  }, [v3LeadsEnabled]);

  // Only a direct click authorizes the frozen scope. The server reclassifies
  // current evidence before publishing any work.
  const startV3Workflow = async () => {
    if (workflowBusy || !selectedCounties.length) return;
    setV3Preparing(true);
    effectiveLeadsLoad.reset();
    loadedWorkflow.current = null;
    try {
      const scope = { ...pendingEmailScope, countyIds: [...pendingEmailScope.countyIds] };
      const res = await scrapingService.createV3LeadsJob({ ...scope, includeEntities,
        endToEnd: true, batchdataEnabled: enableBatchDataEnrichment,
        zerobounceEnabled: enableZeroBounceVerification, verifyAllLegacyEmails,
        confirmLive: true,
      });
      if (!res.jobCreated || !res.jobId) throw new Error('The server did not create a durable workflow. Check that the matching backend is installed.');
      setWorkflowScope(scope);
      setWorkflowJob(null);
      setV3ActiveJobId(res.jobId);
      storeActiveJobId(res.jobId);
    } catch (err) { toast.error(err.message || 'Unable to prepare enrichment'); }
    finally { setV3Preparing(false); }
  };

  const handleV3JobCleared = () => {
    setV3ActiveJobId(null);
    setWorkflowJob(null);
    setWorkflowScope(null);
    loadedWorkflow.current = null;
    effectiveLeadsLoad.reset();
    clearActiveJobId();
  };

  const handleWorkflowStatus = (job) => {
    if (workflowJob && isTerminalWorkflow(workflowJob) && !isTerminalWorkflow(job)) {
      loadedWorkflow.current = null;
      effectiveLeadsLoad.reset();
    }
    setWorkflowJob(job);
    setWorkflowScope((previous) => {
      const scope = { countyIds: job.countyIds, filterMode: job.filterMode, minAcres: job.minAcres, maxAcres: job.maxAcres };
      return JSON.stringify(previous) === JSON.stringify(scope) ? previous : scope;
    });
  };

return (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex">
    <Toaster position="top-right" toastOptions={{ duration: 3500, style: { background: '#111827', color: '#fff' } }} />
    
    {/* Left Sidebar */}
    <div className="w-64 bg-white border-r border-gray-200 hidden md:block">
      <div className="p-4 border-b border-gray-200">
        <h1 className="text-xl font-bold text-blue-600">LeadScraper Pro</h1>
      </div>
      <nav className="p-2">
        <ul className="space-y-1">
          <li>
            <button 
              onClick={() => setActiveTab('leads')}
              className={`w-full text-left px-4 py-2 rounded-md flex items-center ${activeTab === 'leads' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            >
              <FaUsers className="mr-3" /> Leads
            </button>
          </li>
          <li>
            <button 
              onClick={() => setActiveTab('costAnalytics')}
              className={`w-full text-left px-4 py-2 rounded-md flex items-center ${activeTab === 'costAnalytics' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            >
              <FaDollarSign className="mr-3" /> Cost Analytics
            </button>
          </li>
          <li>
            <button 
              onClick={() => setActiveTab('dataQuery')}
              className={`w-full text-left px-4 py-2 rounded-md flex items-center ${activeTab === 'dataQuery' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            >
              <FaRobot className="mr-3" /> Data Query
            </button>
          </li>
          <li>
            <button 
              onClick={() => setActiveTab('batchDataV3')}
              className={`w-full text-left px-4 py-2 rounded-md flex items-center ${activeTab === 'batchDataV3' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            >
              <AiFillThunderbolt className="mr-3" /> V3 Recovery
            </button>
          </li>
        </ul>
      </nav>
    </div>

    {/* Mobile menu button */}
    <div className="md:hidden fixed top-4 left-4 z-50">
      <button 
        onClick={() => setMobileMenuOpen(prev => !prev)} 
        className="p-2 rounded-md bg-white shadow"
      >
        {mobileMenuOpen ? <FaTimes /> : <FaBars />}
      </button>
    </div>

    {/* Mobile menu */}
    {mobileMenuOpen && (
      <div className="md:hidden fixed inset-0 z-40 bg-black/50 flex items-start">
        <div className="w-64 bg-white h-full overflow-y-auto">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h1 className="text-xl font-bold text-blue-600">LeadScraper Pro</h1>
            <button onClick={() => setMobileMenuOpen(false)} className="text-gray-500">
              <FaTimes />
            </button>
          </div>
          <nav className="p-2">
            <ul className="space-y-1">
              <li>
                <button 
                  onClick={() => {setActiveTab('leads'); setMobileMenuOpen(false);}}
                  className={`w-full text-left px-4 py-2 rounded-md flex items-center ${activeTab === 'leads' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                >
                  <FaUsers className="mr-3" /> Leads
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {setActiveTab('costAnalytics'); setMobileMenuOpen(false);}}
                  className={`w-full text-left px-4 py-2 rounded-md flex items-center ${activeTab === 'costAnalytics' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                >
                  <FaDollarSign className="mr-3" /> Cost Analytics
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {setActiveTab('dataQuery'); setMobileMenuOpen(false);}}
                  className={`w-full text-left px-4 py-2 rounded-md flex items-center ${activeTab === 'dataQuery' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                >
                  <FaRobot className="mr-3" /> Data Query
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {setActiveTab('batchDataV3'); setMobileMenuOpen(false);}}
                  className={`w-full text-left px-4 py-2 rounded-md flex items-center ${activeTab === 'batchDataV3' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                >
                  <AiFillThunderbolt className="mr-3" /> V3 Recovery
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    )}

    {/* Main Content */}
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white/70 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-30">
        {/* <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full"> */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-xl font-bold text-gray-900 md:hidden">Leads</h1>
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-4">
                {countyName ? `${countyName} County` : 'Select a county'}
              </span>
            </div>
            {/* Start Logout section */}
            {/* <div>
              <button className="p-2 rounded-full hover:bg-gray-100">
                <FaSignOutAlt className="text-gray-600" />
              </button>
            </div> */}

            <div className="flex items-center">
                {currentUser && !currentUser.bypass && (
                  <span className="text-sm text-gray-600 mr-3">
                    {currentUser.name || currentUser.email}
                  </span>
                )}
                <button 
                  onClick={handleLogout} 
                  className="flex items-center px-3 py-2 rounded-md hover:bg-gray-100 text-gray-600"
                >
                  <FaSignOutAlt className="mr-1" />
                  <span className="text-sm">Logout</span>
                </button>
            </div>
            {/* End Logout section */}
          </div>
        </div>
      </header>

      

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* <div className="max-w-4xl mx-auto"> */}
        <div className="max-w-7xl mx-auto">
          {activeTab === 'leads' && (
          <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:space-x-3 space-y-3 sm:space-y-0">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">State</label>
                <select
                  value={stateId}
                  onChange={(e) => setStateId(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" disabled>Select a state</option>
                  {states.map((s) => (
                    <option key={s.state_id} value={s.state_id}>{s.state_name}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">
                  County {selectedCounties.length > 1 && <span className="text-blue-600 font-medium">({selectedCounties.length} selected)</span>}
                </label>
                <MultiCountySelect
                  counties={counties}
                  selectedCounties={selectedCounties}
                  onChange={(selected) => {
                    setSelectedCounties(selected);
                    // Clear stats when selection changes
                    setCountyStats({});
                    setAllEnrichedRecords([]);
                    setAllNonEnrichedRecords([]);
                    setEnrichedRecords([]);
                  }}
                  disabled={!stateId || loadingRawData}
                />
              </div>
              {/* <div>
                <label className="block text-sm text-gray-600 mb-1">How many</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={limit}
                  onChange={(e) => setLimit(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
                  className="w-24 border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div> */}

              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">Acres Filter</label>
                <select
                  value={acresFilterMode}
                  onChange={(e) => setAcresFilterMode(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="atleast">At least</option>
                  <option value="atmax">At max</option>
                  <option value="between">In between</option>
                </select>
              </div>

                {/* Start of new acreage inputs */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    {acresFilterMode === 'atleast' ? 'Minimum acres' : 
                    acresFilterMode === 'atmax' ? 'Maximum acres' : 'Min acres'}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={acresFilterMode === 'atmax' ? maxAcres : minAcres}
                    onChange={(e) => {
                      const value = e.target.value === '' ? '' : Number(e.target.value);
                      if (acresFilterMode === 'atmax') {
                        setMaxAcres(value);
                      } else {
                        // Allow 0 as a valid value, only default to 0 if empty
                        setMinAcres(value === '' ? 0 : value);
                      }
                    }}
                    className="w-28 border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {acresFilterMode === 'between' && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Max acres</label>
                    <input
                      type="number"
                      min={minAcres}
                      value={maxAcres}
                      onChange={(e) => setMaxAcres(e.target.value === '' ? '' : Number(e.target.value))}
                      placeholder="unlimited"
                      className="w-28 border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* REMOVED: The disabled input for 'atmax' mode */}
                {/* End of new acreage inputs */}

              </div>

              {/* NEW: County acreage range display (single county backward compatibility) */}
              {selectedCounties.length === 1 && countyMinAcres !== null && countyMaxAcres !== null && (
                <div className="mt-3 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2">
                  <span className="font-medium">📏 Acreage range for {countyName}:</span>
                  <span className="ml-2">
                    Min: <span className="font-semibold text-gray-800">{countyMinAcres.toLocaleString()}</span> acres
                  </span>
                  <span className="mx-2">|</span>
                  <span>
                    Max: <span className="font-semibold text-gray-800">{countyMaxAcres.toLocaleString()}</span> acres
                  </span>
                </div>
              )}

              {/* NEW: Multi-County Info Cards Grid */}
              {selectedCounties.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-medium text-gray-700 mb-3">
                    📊 Selected Counties ({selectedCounties.length})
                  </div>
                  <div className={`grid gap-4 ${
                    selectedCounties.length === 1 ? 'grid-cols-1 max-w-md' :
                    selectedCounties.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                    'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
                  }`}>
                    {selectedCounties.map((county) => (
                      <CountyInfoCard
                        key={county.county_id}
                        county={county}
                        stats={countyStats[county.county_id]}
                        isLoading={countyStats[county.county_id]?.loading}
                        isProcessing={enrichmentProgress.isProcessing && 
                          enrichmentProgress.currentCountyName === county.county_name}
                        isCompleted={enrichmentProgress.completedCounties.includes(county.county_id)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* NEW: Filter Summary for All Counties (Aggregate) */}
              {selectedCounties.length > 0 && Object.keys(countyStats).length > 0 && (
                <div className="mt-3 text-sm bg-indigo-50 border border-indigo-200 rounded-md p-3">
                  <div className="font-medium text-indigo-800 mb-2">
                    🔍 Filter Applied:
                    <span className="ml-2 font-normal text-indigo-600">
                      {acresFilterMode === 'atleast' && `At least ${minAcres} acres`}
                      {acresFilterMode === 'atmax' && `At max ${maxAcres || 'unlimited'} acres`}
                      {acresFilterMode === 'between' && `Between ${minAcres} - ${maxAcres || 'unlimited'} acres`}
                    </span>
                  </div>
                  
                  {/* Aggregate totals */}
                  {(() => {
                    // filteredNonEnriched is the TOTAL pool (master list), filteredEnriched is the subset that's been enriched
                    const totalInRange = Object.values(countyStats)
                      .reduce((sum, s) => sum + (s.filteredNonEnriched || 0), 0);
                    const totalEnriched = Object.values(countyStats)
                      .reduce((sum, s) => sum + (s.filteredEnriched || 0), 0);
                    const awaitingEnrichment = Math.max(0, totalInRange - totalEnriched);
                    const enrichmentPct = totalInRange > 0 ? ((totalEnriched / totalInRange) * 100).toFixed(1) : 0;
                    
                    return (
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-md p-2 border border-indigo-100">
                          <div className="text-xs text-gray-500 uppercase">Total In Range</div>
                          <div className="text-lg font-bold text-indigo-700">
                            {totalInRange.toLocaleString()}
                          </div>
                        </div>
                        <div className="bg-white rounded-md p-2 border border-green-100">
                          <div className="text-xs text-gray-500 uppercase">Enriched ({enrichmentPct}%)</div>
                          <div className="text-lg font-bold text-green-700">
                            {totalEnriched.toLocaleString()}
                          </div>
                        </div>
                        <div className="bg-white rounded-md p-2 border border-amber-100">
                          <div className="text-xs text-gray-500 uppercase">Awaiting Enrichment</div>
                          <div className="text-lg font-bold text-amber-700">
                            {awaitingEnrichment.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Unverified count display */}
                  {(() => {
                    const totalUnverified = Object.values(countyStats)
                      .reduce((sum, s) => sum + (s.unverifiedCount || 0), 0);
                    const totalEnriched = Object.values(countyStats)
                      .reduce((sum, s) => sum + (s.filteredEnriched || 0), 0);
                    
                    if (totalUnverified > 0) {
                      return (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                          <div className="flex items-center text-amber-800 text-sm">
                            <span className="mr-2">⚠️</span>
                            <span>
                              <strong>{totalUnverified.toLocaleString()}</strong> of {totalEnriched.toLocaleString()} enriched records 
                              have stored email inventory pending verification
                            </span>
                          </div>
                          <div className="text-xs text-amber-600 mt-1 ml-6">
                            Inventory can include suppressed contacts. Use "Verify Pending Emails" to review only effective emails eligible for verification.
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}

              {/* NEW: Selected Counties Summary Message (when > 1 county) */}
              {selectedCounties.length > 1 && (
                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
                  <span className="font-medium text-blue-800">📋 Selected counties: </span>
                  <span className="text-blue-700">
                    {selectedCounties.map(c => c.county_name).join(', ')}
                  </span>
                </div>
              )}

            {/* End Statistics Display */}
            
            {/* Enrichment Options */}
            <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
              <div className="text-sm font-medium text-gray-700 mb-3">Processing Options</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {/* BatchData Enrichment Option */}
                <label 
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
                    enableBatchDataEnrichment 
                      ? 'border-purple-400 bg-purple-100/50 shadow-sm' 
                      : 'border-gray-200 bg-white hover:border-purple-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={enableBatchDataEnrichment}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEnableBatchDataEnrichment(checked);
                    }}
                    className="sr-only"
                  />
                  <div className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center ${
                    enableBatchDataEnrichment ? 'bg-purple-600 text-white' : 'bg-gray-200'
                  }`}>
                    {enableBatchDataEnrichment && <FaCheckSquare className="w-4 h-4" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <FaDatabase className={`text-lg ${enableBatchDataEnrichment ? 'text-purple-600' : 'text-gray-400'}`} />
                    <div>
                      <div className={`font-medium ${enableBatchDataEnrichment ? 'text-purple-800' : 'text-gray-600'}`}>
                        Enrich with BatchData V3
                      </div>
                      <div className="text-xs text-gray-500">Find emails, phones & owner info</div>
                    </div>
                  </div>
                </label>

                {/* ZeroBounce Verification Option */}
                <label 
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                    enableZeroBounceVerification
                        ? 'border-blue-400 bg-blue-100/50 shadow-sm cursor-pointer' 
                        : 'border-gray-200 bg-white hover:border-blue-200 cursor-pointer'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={enableZeroBounceVerification}
                    onChange={(e) => setEnableZeroBounceVerification(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center ${
                    enableZeroBounceVerification ? 'bg-blue-600 text-white' : 'bg-gray-200'
                  }`}>
                    {enableZeroBounceVerification && <FaCheckSquare className="w-4 h-4" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <MdVerified className={`text-xl ${enableZeroBounceVerification ? 'text-blue-600' : 'text-gray-400'}`} />
                    <div>
                      <div className={`font-medium ${enableZeroBounceVerification ? 'text-blue-800' : 'text-gray-600'}`}>
                        Verify with ZeroBounce
                      </div>
                      <div className="text-xs text-gray-500">Verify current emails after enrichment finishes.</div>
                    </div>
                  </div>
                </label>

                {v3LeadsEnabled && <>
                  <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer ${verifyAllLegacyEmails ? 'border-blue-400 bg-blue-100/50 shadow-sm' : 'border-gray-200 bg-white'}`}>
                    <input type="checkbox" checked={verifyAllLegacyEmails} onChange={(e) => setVerifyAllLegacyEmails(e.target.checked)} className="w-5 h-5" />
                    <MdVerified className="text-blue-600 text-xl flex-shrink-0" />
                    <div><div className="font-medium text-blue-800">Verify all legacy emails</div>
                      <div className="text-xs text-gray-500">All current email slots, including weak matches. Lead qualification stays the same.</div></div>
                  </label>
                  <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer ${includeEntities ? 'border-teal-400 bg-teal-100/50 shadow-sm' : 'border-gray-200 bg-white'}`}>
                    <input type="checkbox" checked={includeEntities} onChange={(e) => setIncludeEntities(e.target.checked)} className="w-5 h-5" />
                    <FaBuilding className="text-teal-600 text-xl flex-shrink-0" />
                    <div><div className="font-medium text-teal-800">Include business/entity contacts</div>
                      <div className="text-xs text-gray-500">Preserve all contacts found for entity-owned parcels</div></div>
                  </label>
                </>}

              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              {/* Dynamic "Get Enriched Leads" button */}
              <button
                onClick={v3LeadsEnabled ? startV3Workflow : getCompleteEnrichedLeads}
                disabled={(v3LeadsEnabled ? workflowBusy : loadingRawData) || selectedCounties.length === 0 || (v3LeadsEnabled ? (!enableBatchDataEnrichment && !enableZeroBounceVerification) : !enableBatchDataEnrichment)}
                className={`px-5 py-3 rounded-lg text-white font-medium transition-all ${
                  (v3LeadsEnabled ? workflowBusy : loadingRawData) || selectedCounties.length === 0 || (v3LeadsEnabled ? (!enableBatchDataEnrichment && !enableZeroBounceVerification) : !enableBatchDataEnrichment)
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : enableZeroBounceVerification
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-md hover:shadow-lg'
                      : 'bg-purple-600 hover:bg-purple-700 shadow-md hover:shadow-lg'
                } flex items-center justify-center gap-2`}
              >
                {(v3LeadsEnabled ? workflowBusy : loadingRawData) ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>{v3LeadsEnabled ? (v3Preparing ? 'Preparing scope...' : 'Processing...') : `Processing${selectedCounties.length > 1 ? ` (${enrichmentProgress.currentCountyIndex + 1}/${selectedCounties.length})` : '...'}`}</span>
                  </>
                ) : (
                  <>
                    <AiFillThunderbolt className="text-lg" />
                    <span>
                      {v3LeadsEnabled || enableZeroBounceVerification ? 'Enrich & Verify' : 'Enrich Only'}
                      {selectedCounties.length > 1 ? ` (${selectedCounties.length} counties)` : ''}
                    </span>
                  </>
                )}
              </button>
            </div>

            {v3LeadsEnabled && effectiveLeadsLoad.status !== 'idle' && (
              <div className="mt-3 text-sm" role="status" aria-live="polite">
                {loadingEffectiveLeads ? (
                  <>
                    Loading {effectiveLeadsLoad.loaded.toLocaleString()} of {effectiveLeadsLoad.total?.toLocaleString() ?? '…'} effective email rows…
                    <button type="button" onClick={effectiveLeadsLoad.cancel} className="ml-3 underline text-blue-700">Cancel loading</button>
                  </>
                ) : effectiveLeadsLoad.status === 'complete' ? (
                  <>Ready for filtering/export — all {effectiveLeadsLoad.total.toLocaleString()} effective email rows</>
                ) : (
                  <>Incomplete: {effectiveLeadsLoad.loaded.toLocaleString()} of {effectiveLeadsLoad.total?.toLocaleString() ?? 'unknown'} effective email rows retrieved. {effectiveLeadsLoad.error}<button type="button" onClick={loadWorkflowLeads} className="ml-3 underline">Retry loading results</button></>
                )}
              </div>
            )}

            {/* Enhanced loading indicator for multi-county processing */}
            {loadingRawData && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center mb-2">
                  <div className="flex space-x-1 mr-3">
                    <span className="inline-block w-2 h-2 bg-blue-600 rounded-full animate-bounce"></span>
                    <span className="inline-block w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                    <span className="inline-block w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                  </div>
                  <div className="text-blue-700 font-medium">
                    {enrichmentProgress.totalCounties > 1 ? (
                      <>
                        Processing {enrichmentProgress.currentCountyName} 
                        ({enrichmentProgress.currentCountyIndex + 1} of {enrichmentProgress.totalCounties})
                      </>
                    ) : (
                      'Processing in progress'
                    )}
                  </div>
                </div>
                
                {/* Progress bar for multi-county */}
                {enrichmentProgress.totalCounties > 1 && (
                  <div className="mb-2">
                    <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 transition-all duration-500"
                        style={{ 
                          width: `${((enrichmentProgress.completedCounties.length) / enrichmentProgress.totalCounties) * 100}%` 
                        }}
                      />
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      {enrichmentProgress.completedCounties.length} of {enrichmentProgress.totalCounties} counties completed
                    </div>
                  </div>
                )}
                
                <div className="text-sm text-blue-600">
                  <BatchDataProcessingStatus />
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500 mt-4">Starting authorizes the selected providers for this scope. Saved matching verification results are reused before paid calls.</p>

            {v3LeadsEnabled && v3ActiveJobId && (
              <div className="mt-6">
                <V3LeadsJobProgress
                  key={v3ActiveJobId}
                  jobId={v3ActiveJobId}
                  onJobCleared={handleV3JobCleared}
                  onStatus={handleWorkflowStatus}
                  resultsStatus={effectiveLeadsLoad.status}
                  recoveryScope={workflowScope || pendingEmailScope}
                  onRecoveryResultsAvailable={loadWorkflowLeads}
                />
              </div>
            )}

            {v3LeadsEnabled && !v3ActiveJobId && <V3UncertainRecoveryPanel
              scope={pendingEmailScope} onResultsAvailable={loadWorkflowLeads} />}


          </div>
          )}

          {/* Combined elements of the leads tab */}
          {activeTab === 'leads' && (
          <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
            {/* Form controls */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:space-x-3 space-y-3 sm:space-y-0">
              {/* Your existing form controls */}
            </div>
            
            <div className="mt-6 flex flex-col sm:flex-row justify-center sm:justify-start gap-4">
              {/* Your existing button */}
            </div>

            {/* Loading indicator */}
            {loadingRawData && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                {/* ... */}
              </div>
            )}

            {/* EmailsTable component */}
            {v3LeadsEnabled ? (
              Array.isArray(effectiveLeadRows) && (
                <div className="mt-6 w-full">
                  <h3 className="text-lg font-semibold mb-4">Effective Leads</h3>
                  <EmailsTable key={`${effectiveLeadsLoad.signature}:${effectiveLeadsLoad.loadId}`}
                    leadRows={effectiveLeadRows} loadState={effectiveLeadsLoad} />
                </div>
              )
            ) : (
              allEnrichedRecords.length > 0 && (
                <div className="mt-6 w-full">
                  <h3 className="text-lg font-semibold mb-4">Extracted Email Records</h3>
                  <EmailsTable
                    enrichedRecords={allEnrichedRecords}
                    nonEnrichedRecords={allNonEnrichedRecords}
                  />
                </div>
              )
            )}

            <p className="text-xs text-gray-500 mt-4">Click "Enrich Only" or "Enrich & Verify" to process leads.</p>
          </div>
        )}


          {activeTab === 'costAnalytics' && (
            <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
              <CostAnalyticsPanel />
            </div>
          )}

          {activeTab === 'dataQuery' && (
            <div className="h-[calc(100vh-150px)]">
              <DataQueryPanel />
            </div>
          )}

          {activeTab === 'batchDataV3' && (
            <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
              <BatchDataV3Panel />
            </div>
          )}

          {/* Keep your debug section */}
          {isDebug && (
            <div className="mt-8 space-y-6">
              {/* ... (existing debug content) */}
            </div>
          )}
        </div>
      </main>

      {/* Keep your modals */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
          {/* ... (existing modal content) */}
        </div>
      )}

      <Chatbot />
    </div>
  </div>
);

}

export default MainApp;
